import { createApiRef, createRouteRef, useApi, configApiRef, createPlugin, createApiFactory, discoveryApiRef, identityApiRef, createRoutableExtension, createComponentExtension, useRouteRef } from '@backstage/core-plugin-api';
import { ResponseError, NotFoundError } from '@backstage/errors';
import { EventSourcePolyfill } from 'event-source-polyfill';
import * as React from 'react';
import React__default, { useEffect, useState, useReducer, useRef, useMemo, useCallback } from 'react';
import { useCopyToClipboard, useAsyncRetry, useAsync } from 'react-use';
import { capitalize } from 'lodash';
import { SubvalueCell, Link, Table, EmptyState, Button, WarningPanel, CodeSnippet, PageWithHeader, Content, ContentHeader, SupportButton, ErrorPage, Progress, ItemCardGrid, ItemCardHeader, HeaderTabs, HeaderLabel, Header, Page, MissingAnnotationEmptyState } from '@backstage/core-components';
import { favoriteEntityIcon, favoriteEntityTooltip, EntityRefLinks, getEntityRelations, formatEntityRefTitle, useEntityListProvider, useStarredEntities, CATALOG_FILTER_EXISTS, EntityListProvider, UserListPicker, EntityOwnerPicker, EntityTagPicker, catalogApiRef, useOwnUser, isOwnerOf, EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import { generatePath, useParams, useNavigate, Routes, Route } from 'react-router-dom';
import { RELATION_OWNED_BY } from '@backstage/catalog-model';
import ShareIcon from '@material-ui/icons/Share';
import { FilteredEntityLayout, FilterContainer, EntityListContainer } from '@backstage/plugin-catalog';
import { makeStyles, ListItem, ListItemText, Divider, createStyles, Button as Button$1, Drawer, Grid, Typography, IconButton, useTheme, CircularProgress, Card, CardMedia, CardContent, CardActions } from '@material-ui/core';
import TextTruncate from 'react-text-truncate';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { Alert } from '@material-ui/lab';
import FeedbackOutlinedIcon from '@material-ui/icons/FeedbackOutlined';
import ReactDOM from 'react-dom';
import parseGitUrl from 'git-url-parse';
import DOMPurify from 'dompurify';
import Close from '@material-ui/icons/Close';
import { LazyLog } from 'react-lazylog';
import { useOutlet } from 'react-router';
import CodeIcon from '@material-ui/icons/Code';

const techdocsStorageApiRef = createApiRef({
  id: "plugin.techdocs.storageservice",
  description: "Used to make requests towards the techdocs storage"
});
const techdocsApiRef = createApiRef({
  id: "plugin.techdocs.service",
  description: "Used to make requests towards techdocs API"
});

class TechDocsClient {
  constructor({
    configApi,
    discoveryApi,
    identityApi
  }) {
    this.configApi = configApi;
    this.discoveryApi = discoveryApi;
    this.identityApi = identityApi;
  }
  async getApiOrigin() {
    var _a;
    return (_a = this.configApi.getOptionalString("techdocs.requestUrl")) != null ? _a : await this.discoveryApi.getBaseUrl("techdocs");
  }
  async getTechDocsMetadata(entityId) {
    const {kind, namespace, name} = entityId;
    const apiOrigin = await this.getApiOrigin();
    const requestUrl = `${apiOrigin}/metadata/techdocs/${namespace}/${kind}/${name}`;
    const token = await this.identityApi.getIdToken();
    const request = await fetch(`${requestUrl}`, {
      headers: token ? {Authorization: `Bearer ${token}`} : {}
    });
    if (!request.ok) {
      throw await ResponseError.fromResponse(request);
    }
    return await request.json();
  }
  async getEntityMetadata(entityId) {
    const {kind, namespace, name} = entityId;
    const apiOrigin = await this.getApiOrigin();
    const requestUrl = `${apiOrigin}/metadata/entity/${namespace}/${kind}/${name}`;
    const token = await this.identityApi.getIdToken();
    const request = await fetch(`${requestUrl}`, {
      headers: token ? {Authorization: `Bearer ${token}`} : {}
    });
    if (!request.ok) {
      throw await ResponseError.fromResponse(request);
    }
    return await request.json();
  }
}
class TechDocsStorageClient {
  constructor({
    configApi,
    discoveryApi,
    identityApi
  }) {
    this.configApi = configApi;
    this.discoveryApi = discoveryApi;
    this.identityApi = identityApi;
  }
  async getApiOrigin() {
    var _a;
    return (_a = this.configApi.getOptionalString("techdocs.requestUrl")) != null ? _a : await this.discoveryApi.getBaseUrl("techdocs");
  }
  async getStorageUrl() {
    var _a;
    return (_a = this.configApi.getOptionalString("techdocs.storageUrl")) != null ? _a : `${await this.discoveryApi.getBaseUrl("techdocs")}/static/docs`;
  }
  async getBuilder() {
    return this.configApi.getString("techdocs.builder");
  }
  async getEntityDocs(entityId, path) {
    const {kind, namespace, name} = entityId;
    const storageUrl = await this.getStorageUrl();
    const url = `${storageUrl}/${namespace}/${kind}/${name}/${path}`;
    const token = await this.identityApi.getIdToken();
    const request = await fetch(`${url.endsWith("/") ? url : `${url}/`}index.html`, {
      headers: token ? {Authorization: `Bearer ${token}`} : {}
    });
    let errorMessage = "";
    switch (request.status) {
      case 404:
        errorMessage = "Page not found. ";
        if (!path) {
          errorMessage += "This could be because there is no index.md file in the root of the docs directory of this repository.";
        }
        throw new NotFoundError(errorMessage);
      case 500:
        errorMessage = "Could not generate documentation or an error in the TechDocs backend. ";
        throw new Error(errorMessage);
    }
    return request.text();
  }
  async syncEntityDocs(entityId, logHandler = () => {
  }) {
    const {kind, namespace, name} = entityId;
    const apiOrigin = await this.getApiOrigin();
    const url = `${apiOrigin}/sync/${namespace}/${kind}/${name}`;
    const token = await this.identityApi.getIdToken();
    return new Promise((resolve, reject) => {
      const source = new EventSourcePolyfill(url, {
        withCredentials: true,
        headers: token ? {Authorization: `Bearer ${token}`} : {}
      });
      source.addEventListener("log", (e) => {
        if (e.data) {
          logHandler(JSON.parse(e.data));
        }
      });
      source.addEventListener("finish", (e) => {
        let updated = false;
        if (e.data) {
          ({updated} = JSON.parse(e.data));
        }
        resolve(updated ? "updated" : "cached");
      });
      source.onerror = (e) => {
        source.close();
        switch (e.status) {
          case 404:
            reject(new NotFoundError(e.message));
            return;
          default:
            reject(new Error(e.data));
            return;
        }
      };
    });
  }
  async getBaseUrl(oldBaseUrl, entityId, path) {
    const {kind, namespace, name} = entityId;
    const apiOrigin = await this.getApiOrigin();
    return new URL(oldBaseUrl, `${apiOrigin}/static/docs/${namespace}/${kind}/${name}/${path}`).toString();
  }
}

const rootRouteRef = createRouteRef({
  path: "",
  title: "TechDocs Landing Page"
});
const rootDocsRouteRef = createRouteRef({
  path: ":namespace/:kind/:name/*",
  title: "Docs"
});
const rootCatalogDocsRouteRef = createRouteRef({
  path: "*",
  title: "Docs"
});

function createCopyDocsUrlAction(copyToClipboard) {
  return (row) => {
    return {
      icon: () => /* @__PURE__ */ React__default.createElement(ShareIcon, {
        fontSize: "small"
      }),
      tooltip: "Click to copy documentation link to clipboard",
      onClick: () => copyToClipboard(`${window.location.origin}${window.location.pathname.replace(/\/?$/, "/")}${row.resolved.docsUrl}`)
    };
  };
}
function createStarEntityAction(isStarredEntity, toggleStarredEntity) {
  return ({entity}) => {
    const isStarred = isStarredEntity(entity);
    return {
      cellStyle: {paddingLeft: "1em"},
      icon: () => favoriteEntityIcon(isStarred),
      tooltip: favoriteEntityTooltip(isStarred),
      onClick: () => toggleStarredEntity(entity)
    };
  };
}

var actionFactories = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createCopyDocsUrlAction: createCopyDocsUrlAction,
  createStarEntityAction: createStarEntityAction
});

function createNameColumn() {
  return {
    title: "Document",
    field: "entity.metadata.name",
    highlight: true,
    render: (row) => /* @__PURE__ */ React__default.createElement(SubvalueCell, {
      value: /* @__PURE__ */ React__default.createElement(Link, {
        to: row.resolved.docsUrl
      }, row.entity.metadata.name),
      subvalue: row.entity.metadata.description
    })
  };
}
function createOwnerColumn() {
  return {
    title: "Owner",
    field: "resolved.ownedByRelationsTitle",
    render: ({resolved}) => /* @__PURE__ */ React__default.createElement(EntityRefLinks, {
      entityRefs: resolved.ownedByRelations,
      defaultKind: "group"
    })
  };
}
function createTypeColumn() {
  return {
    title: "Type",
    field: "entity.spec.type"
  };
}

var columnFactories = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createNameColumn: createNameColumn,
  createOwnerColumn: createOwnerColumn,
  createTypeColumn: createTypeColumn
});

const DocsTable$1 = ({
  entities,
  title,
  loading,
  columns,
  actions
}) => {
  const [, copyToClipboard] = useCopyToClipboard();
  if (!entities)
    return null;
  const documents = entities.map((entity) => {
    var _a;
    const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
    return {
      entity,
      resolved: {
        docsUrl: generatePath(rootDocsRouteRef.path, {
          namespace: (_a = entity.metadata.namespace) != null ? _a : "default",
          kind: entity.kind,
          name: entity.metadata.name
        }),
        ownedByRelations,
        ownedByRelationsTitle: ownedByRelations.map((r) => formatEntityRefTitle(r, {defaultKind: "group"})).join(", ")
      }
    };
  });
  const defaultColumns = [
    createNameColumn(),
    createOwnerColumn(),
    createTypeColumn()
  ];
  const defaultActions = [
    createCopyDocsUrlAction(copyToClipboard)
  ];
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, loading || documents && documents.length > 0 ? /* @__PURE__ */ React__default.createElement(Table, {
    isLoading: loading,
    options: {
      paging: true,
      pageSize: 20,
      search: true,
      actionsColumnIndex: -1
    },
    data: documents,
    columns: columns || defaultColumns,
    actions: actions || defaultActions,
    title: title ? `${title} (${documents.length})` : `All (${documents.length})`
  }) : /* @__PURE__ */ React__default.createElement(EmptyState, {
    missing: "data",
    title: "No documents to show",
    description: "Create your own document. Check out our Getting Started Information",
    action: /* @__PURE__ */ React__default.createElement(Button, {
      color: "primary",
      to: "https://backstage.io/docs/features/techdocs/getting-started",
      variant: "contained"
    }, "DOCS")
  }));
};

var DocsTable$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DocsTable: DocsTable$1
});

const EntityListDocsTable = ({
  columns,
  actions
}) => {
  var _a, _b;
  const {loading, error, entities, filters} = useEntityListProvider();
  const {isStarredEntity, toggleStarredEntity} = useStarredEntities();
  const [, copyToClipboard] = useCopyToClipboard();
  const title = capitalize((_b = (_a = filters.user) == null ? void 0 : _a.value) != null ? _b : "all");
  const defaultActions = [
    createCopyDocsUrlAction(copyToClipboard),
    createStarEntityAction(isStarredEntity, toggleStarredEntity)
  ];
  if (error) {
    return /* @__PURE__ */ React__default.createElement(WarningPanel, {
      severity: "error",
      title: "Could not load available documentation."
    }, /* @__PURE__ */ React__default.createElement(CodeSnippet, {
      language: "text",
      text: error.toString()
    }));
  }
  return /* @__PURE__ */ React__default.createElement(DocsTable$1, {
    title,
    entities,
    loading,
    actions: actions || defaultActions,
    columns
  });
};
EntityListDocsTable.columns = columnFactories;
EntityListDocsTable.actions = actionFactories;

const TechDocsPageWrapper = ({children}) => {
  var _a;
  const configApi = useApi(configApiRef);
  const generatedSubtitle = `Documentation available in ${(_a = configApi.getOptionalString("organization.name")) != null ? _a : "Backstage"}`;
  return /* @__PURE__ */ React__default.createElement(PageWithHeader, {
    title: "Documentation",
    subtitle: generatedSubtitle,
    themeId: "documentation"
  }, children);
};

class TechDocsFilter {
  getCatalogFilters() {
    return {
      "metadata.annotations.backstage.io/techdocs-ref": CATALOG_FILTER_EXISTS
    };
  }
}
const TechDocsPicker = () => {
  const {updateFilters} = useEntityListProvider();
  useEffect(() => {
    updateFilters({
      techdocs: new TechDocsFilter()
    });
  }, [updateFilters]);
  return null;
};

const DefaultTechDocsHome = ({
  initialFilter = "all",
  columns,
  actions
}) => {
  return /* @__PURE__ */ React__default.createElement(TechDocsPageWrapper, null, /* @__PURE__ */ React__default.createElement(Content, null, /* @__PURE__ */ React__default.createElement(ContentHeader, {
    title: ""
  }, /* @__PURE__ */ React__default.createElement(SupportButton, null, "Discover documentation in your ecosystem.")), /* @__PURE__ */ React__default.createElement(EntityListProvider, null, /* @__PURE__ */ React__default.createElement(FilteredEntityLayout, null, /* @__PURE__ */ React__default.createElement(FilterContainer, null, /* @__PURE__ */ React__default.createElement(TechDocsPicker, null), /* @__PURE__ */ React__default.createElement(UserListPicker, {
    initialFilter
  }), /* @__PURE__ */ React__default.createElement(EntityOwnerPicker, null), /* @__PURE__ */ React__default.createElement(EntityTagPicker, null)), /* @__PURE__ */ React__default.createElement(EntityListContainer, null, /* @__PURE__ */ React__default.createElement(EntityListDocsTable, {
    actions,
    columns
  }))))));
};

const useStyles$1 = makeStyles({
  flexContainer: {
    flexWrap: "wrap"
  },
  itemText: {
    width: "100%",
    marginBottom: "1rem"
  }
});
const DocsResultListItem = ({
  result,
  lineClamp = 5
}) => {
  const classes = useStyles$1();
  return /* @__PURE__ */ React__default.createElement(Link, {
    to: result.location
  }, /* @__PURE__ */ React__default.createElement(ListItem, {
    alignItems: "flex-start",
    className: classes.flexContainer
  }, /* @__PURE__ */ React__default.createElement(ListItemText, {
    className: classes.itemText,
    primaryTypographyProps: {variant: "h6"},
    primary: `${result.title} | ${result.name} docs `,
    secondary: /* @__PURE__ */ React__default.createElement(TextTruncate, {
      line: lineClamp,
      truncateText: "\u2026",
      text: result.text,
      element: "span"
    })
  })), /* @__PURE__ */ React__default.createElement(Divider, {
    component: "li"
  }));
};

const techdocsPlugin = createPlugin({
  id: "techdocs",
  apis: [
    createApiFactory({
      api: techdocsStorageApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        identityApi: identityApiRef
      },
      factory: ({configApi, discoveryApi, identityApi}) => new TechDocsStorageClient({
        configApi,
        discoveryApi,
        identityApi
      })
    }),
    createApiFactory({
      api: techdocsApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        identityApi: identityApiRef
      },
      factory: ({configApi, discoveryApi, identityApi}) => new TechDocsClient({
        configApi,
        discoveryApi,
        identityApi
      })
    })
  ],
  routes: {
    root: rootRouteRef,
    entityContent: rootCatalogDocsRouteRef
  }
});
const TechdocsPage = techdocsPlugin.provide(createRoutableExtension({
  component: () => Promise.resolve().then(function () { return Router$1; }).then((m) => m.Router),
  mountPoint: rootRouteRef
}));
const EntityTechdocsContent = techdocsPlugin.provide(createRoutableExtension({
  component: () => Promise.resolve().then(function () { return Router$1; }).then((m) => m.EmbeddedDocsRouter),
  mountPoint: rootCatalogDocsRouteRef
}));
const DocsCardGrid$2 = techdocsPlugin.provide(createComponentExtension({
  component: {
    lazy: () => Promise.resolve().then(function () { return DocsCardGrid$1; }).then((m) => m.DocsCardGrid)
  }
}));
const DocsTable = techdocsPlugin.provide(createComponentExtension({
  component: {
    lazy: () => Promise.resolve().then(function () { return DocsTable$2; }).then((m) => m.DocsTable)
  }
}));
const TechDocsCustomHome$2 = techdocsPlugin.provide(createRoutableExtension({
  component: () => Promise.resolve().then(function () { return TechDocsCustomHome$1; }).then((m) => m.TechDocsCustomHome),
  mountPoint: rootRouteRef
}));
const TechDocsIndexPage$2 = techdocsPlugin.provide(createRoutableExtension({
  component: () => Promise.resolve().then(function () { return TechDocsIndexPage$1; }).then((m) => m.TechDocsIndexPage),
  mountPoint: rootRouteRef
}));
const TechDocsReaderPage = techdocsPlugin.provide(createRoutableExtension({
  component: () => Promise.resolve().then(function () { return TechDocsPage$1; }).then((m) => m.TechDocsPage),
  mountPoint: rootDocsRouteRef
}));

const isSvgNeedingInlining = (attrName, attrVal, apiOrigin) => {
  const isSrcToSvg = attrName === "src" && attrVal.endsWith(".svg");
  const isRelativeUrl = !attrVal.match(/^([a-z]*:)?\/\//i);
  const pointsToOurBackend = attrVal.startsWith(apiOrigin);
  return isSrcToSvg && (isRelativeUrl || pointsToOurBackend);
};
const addBaseUrl = ({
  techdocsStorageApi,
  entityId,
  path
}) => {
  return async (dom) => {
    const apiOrigin = await techdocsStorageApi.getApiOrigin();
    const updateDom = async (list, attributeName) => {
      for (const elem of list) {
        if (elem.hasAttribute(attributeName)) {
          const elemAttribute = elem.getAttribute(attributeName);
          if (!elemAttribute)
            return;
          const newValue = await techdocsStorageApi.getBaseUrl(elemAttribute, entityId, path);
          if (isSvgNeedingInlining(attributeName, elemAttribute, apiOrigin)) {
            try {
              const svg = await fetch(newValue, {credentials: "include"});
              const svgContent = await svg.text();
              elem.setAttribute(attributeName, `data:image/svg+xml;base64,${btoa(svgContent)}`);
            } catch (e) {
              elem.setAttribute("alt", `Error: ${elemAttribute}`);
            }
          } else {
            elem.setAttribute(attributeName, newValue);
          }
        }
      }
    };
    await Promise.all([
      updateDom(dom.querySelectorAll("img"), "src"),
      updateDom(dom.querySelectorAll("script"), "src"),
      updateDom(dom.querySelectorAll("link"), "href"),
      updateDom(dom.querySelectorAll("a[download]"), "href")
    ]);
    return dom;
  };
};

const addGitFeedbackLink = (scmIntegrationsApi) => {
  return (dom) => {
    const sourceAnchor = dom.querySelector('[title="Edit this page"]');
    if (!sourceAnchor || !sourceAnchor.href) {
      return dom;
    }
    const sourceURL = new URL(sourceAnchor.href);
    const integration = scmIntegrationsApi.byUrl(sourceURL);
    if ((integration == null ? void 0 : integration.type) !== "github" && (integration == null ? void 0 : integration.type) !== "gitlab") {
      return dom;
    }
    const title = dom.querySelector("article>h1").childNodes[0].textContent;
    const issueTitle = encodeURIComponent(`Documentation Feedback: ${title}`);
    const issueDesc = encodeURIComponent(`Page source:
${sourceAnchor.href}

Feedback:`);
    const gitInfo = parseGitUrl(sourceURL.pathname);
    const repoPath = `/${gitInfo.organization}/${gitInfo.name}`;
    const feedbackLink = sourceAnchor.cloneNode();
    switch (integration == null ? void 0 : integration.type) {
      case "gitlab":
        feedbackLink.href = `${sourceURL.origin}${repoPath}/issues/new?issue[title]=${issueTitle}&issue[description]=${issueDesc}`;
        break;
      case "github":
        feedbackLink.href = `${sourceURL.origin}${repoPath}/issues/new?title=${issueTitle}&body=${issueDesc}`;
        break;
      default:
        return dom;
    }
    ReactDOM.render(React__default.createElement(FeedbackOutlinedIcon), feedbackLink);
    feedbackLink.style.paddingLeft = "5px";
    feedbackLink.title = "Leave feedback for this page";
    feedbackLink.id = "git-feedback-link";
    sourceAnchor == null ? void 0 : sourceAnchor.insertAdjacentElement("beforebegin", feedbackLink);
    return dom;
  };
};

const rewriteDocLinks = () => {
  return (dom) => {
    const updateDom = (list, attributeName) => {
      Array.from(list).filter((elem) => elem.hasAttribute(attributeName)).forEach((elem) => {
        const elemAttribute = elem.getAttribute(attributeName);
        if (elemAttribute) {
          if (elemAttribute.match(/^https?:\/\//i)) {
            elem.setAttribute("target", "_blank");
          }
          try {
            const normalizedWindowLocation = normalizeUrl(window.location.href);
            elem.setAttribute(attributeName, new URL(elemAttribute, normalizedWindowLocation).toString());
          } catch (_e) {
            elem.replaceWith(elem.textContent || elemAttribute);
          }
        }
      });
    };
    updateDom(Array.from(dom.getElementsByTagName("a")), "href");
    return dom;
  };
};
function normalizeUrl(input) {
  const url = new URL(input);
  if (!url.pathname.endsWith("/") && !url.pathname.endsWith(".html")) {
    url.pathname += "/";
  }
  return url.toString();
}

const addLinkClickListener = ({
  baseUrl,
  onClick
}) => {
  return (dom) => {
    Array.from(dom.getElementsByTagName("a")).forEach((elem) => {
      elem.addEventListener("click", (e) => {
        const target = elem;
        const href = target.getAttribute("href");
        if (!href)
          return;
        if (href.startsWith(baseUrl)) {
          e.preventDefault();
          onClick(e, href);
        }
      });
    });
    return dom;
  };
};

const removeMkdocsHeader = () => {
  return (dom) => {
    var _a;
    (_a = dom.querySelector(".md-header")) == null ? void 0 : _a.remove();
    return dom;
  };
};

const simplifyMkdocsFooter = () => {
  return (dom) => {
    var _a;
    (_a = dom.querySelector(".md-footer-copyright")) == null ? void 0 : _a.remove();
    return dom;
  };
};

const onCssReady = ({
  docStorageUrl,
  onLoading,
  onLoaded
}) => {
  return (dom) => {
    const cssPages = Array.from(dom.querySelectorAll('head > link[rel="stylesheet"]')).filter((elem) => {
      var _a;
      return (_a = elem.getAttribute("href")) == null ? void 0 : _a.startsWith(docStorageUrl);
    });
    let count = cssPages.length;
    if (count > 0) {
      onLoading(dom);
    }
    cssPages.forEach((cssPage) => cssPage.addEventListener("load", () => {
      count -= 1;
      if (count === 0) {
        onLoaded(dom);
      }
    }));
    return dom;
  };
};

const TECHDOCS_CSS = /main\.[A-Fa-f0-9]{8}\.min\.css$/;
const GOOGLE_FONTS = /^https:\/\/fonts\.googleapis\.com/;
const GSTATIC_FONTS = /^https:\/\/fonts\.gstatic\.com/;
const safeLinksHook = (node) => {
  if (node.nodeName && node.nodeName === "LINK") {
    const href = node.getAttribute("href") || "";
    if (href.match(TECHDOCS_CSS)) {
      node.setAttribute("rel", "stylesheet");
    }
    if (href.match(GOOGLE_FONTS)) {
      node.setAttribute("rel", "stylesheet");
    }
    if (href.match(GSTATIC_FONTS)) {
      node.setAttribute("rel", "preconnect");
    }
  }
  return node;
};
const sanitizeDOM = () => {
  return (dom) => {
    DOMPurify.addHook("afterSanitizeAttributes", safeLinksHook);
    return DOMPurify.sanitize(dom.innerHTML, {
      ADD_TAGS: ["link"],
      FORBID_TAGS: ["style"],
      WHOLE_DOCUMENT: true,
      RETURN_DOM: true
    });
  };
};

const injectCss = ({css}) => {
  return (dom) => {
    dom.getElementsByTagName("head")[0].insertAdjacentHTML("beforeend", `<style>${css}</style>`);
    return dom;
  };
};

const transform = async (html, transformers) => {
  let dom;
  if (typeof html === "string") {
    dom = new DOMParser().parseFromString(html, "text/html").documentElement;
  } else if (html instanceof Element) {
    dom = html;
  } else {
    throw new Error("dom is not a recognized type");
  }
  for (const transformer of transformers) {
    dom = await transformer(dom);
  }
  return dom;
};

const useDrawerStyles = makeStyles((theme) => createStyles({
  paper: {
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "75%"
    },
    [theme.breakpoints.up("md")]: {
      width: "50%"
    },
    padding: theme.spacing(2.5)
  },
  root: {
    height: "100%",
    overflow: "hidden"
  }
}));
const TechDocsBuildLogsDrawerContent = ({
  buildLog,
  onClose
}) => {
  const classes = useDrawerStyles();
  return /* @__PURE__ */ React.createElement(Grid, {
    container: true,
    direction: "column",
    className: classes.root,
    spacing: 0,
    wrap: "nowrap"
  }, /* @__PURE__ */ React.createElement(Grid, {
    item: true,
    container: true,
    justifyContent: "space-between",
    alignItems: "center",
    spacing: 0,
    wrap: "nowrap"
  }, /* @__PURE__ */ React.createElement(Typography, {
    variant: "h5"
  }, "Build Details"), /* @__PURE__ */ React.createElement(IconButton, {
    key: "dismiss",
    title: "Close the drawer",
    onClick: onClose,
    color: "inherit"
  }, /* @__PURE__ */ React.createElement(Close, null))), /* @__PURE__ */ React.createElement(LazyLog, {
    text: buildLog.length === 0 ? "Waiting for logs..." : buildLog.join("\n"),
    extraLines: 1,
    follow: true,
    selectableLines: true,
    enableSearch: true
  }));
};
const TechDocsBuildLogs = ({buildLog}) => {
  const classes = useDrawerStyles();
  const [open, setOpen] = useState(false);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Button$1, {
    color: "inherit",
    onClick: () => setOpen(true)
  }, "Show Build Logs"), /* @__PURE__ */ React.createElement(Drawer, {
    classes: {paper: classes.paper},
    anchor: "right",
    open,
    onClose: () => setOpen(false)
  }, /* @__PURE__ */ React.createElement(TechDocsBuildLogsDrawerContent, {
    buildLog,
    onClose: () => setOpen(false)
  })));
};

const TechDocsNotFound = ({errorMessage}) => {
  const techdocsBuilder = useApi(configApiRef).getOptionalString("techdocs.builder");
  let additionalInfo = "";
  if (techdocsBuilder !== "local") {
    additionalInfo = "Note that techdocs.builder is not set to 'local' in your config, which means this Backstage app will not generate docs if they are not found. Make sure the project's docs are generated and published by some external process (e.g. CI/CD pipeline). Or change techdocs.builder to 'local' to generate docs from this Backstage instance.";
  }
  return /* @__PURE__ */ React__default.createElement(ErrorPage, {
    status: "404",
    statusMessage: errorMessage || "Documentation not found",
    additionalInfo
  });
};

function calculateDisplayState({
  contentLoading,
  content,
  activeSyncState
}) {
  if (contentLoading) {
    return "CHECKING";
  }
  if (activeSyncState === "BUILD_READY_RELOAD") {
    return "CHECKING";
  }
  if (!content && activeSyncState === "CHECKING") {
    return "CHECKING";
  }
  if (!content && activeSyncState === "BUILDING") {
    return "INITIAL_BUILD";
  }
  if (!content) {
    return "CONTENT_NOT_FOUND";
  }
  if (activeSyncState === "BUILDING") {
    return "CONTENT_STALE_REFRESHING";
  }
  if (activeSyncState === "BUILD_READY") {
    return "CONTENT_STALE_READY";
  }
  if (activeSyncState === "ERROR") {
    return "CONTENT_STALE_ERROR";
  }
  return "CONTENT_FRESH";
}
function reducer(oldState, action) {
  const newState = {...oldState};
  switch (action.type) {
    case "sync":
      if (action.state === "CHECKING") {
        newState.buildLog = [];
      }
      newState.activeSyncState = action.state;
      newState.syncError = action.syncError;
      break;
    case "contentLoading":
      newState.contentLoading = true;
      newState.contentError = void 0;
      break;
    case "content":
      if (typeof action.path === "string") {
        newState.path = action.path;
      }
      newState.contentLoading = false;
      newState.content = action.content;
      newState.contentError = action.contentError;
      break;
    case "buildLog":
      newState.buildLog = newState.buildLog.concat(action.log);
      break;
    default:
      throw new Error();
  }
  if (["BUILD_READY", "BUILD_READY_RELOAD"].includes(newState.activeSyncState) && ["contentLoading", "content"].includes(action.type)) {
    newState.activeSyncState = "UP_TO_DATE";
    newState.buildLog = [];
  }
  return newState;
}
function useReaderState(kind, namespace, name, path) {
  var _a, _b;
  const [state, dispatch] = useReducer(reducer, {
    activeSyncState: "CHECKING",
    path,
    contentLoading: true,
    buildLog: []
  });
  const techdocsStorageApi = useApi(techdocsStorageApiRef);
  const {retry: contentReload} = useAsyncRetry(async () => {
    dispatch({type: "contentLoading"});
    try {
      const entityDocs = await techdocsStorageApi.getEntityDocs({kind, namespace, name}, path);
      dispatch({type: "content", content: entityDocs, path});
      return entityDocs;
    } catch (e) {
      dispatch({type: "content", contentError: e, path});
    }
    return void 0;
  }, [techdocsStorageApi, kind, namespace, name, path]);
  const contentRef = useRef({
    content: void 0,
    reload: () => {
    }
  });
  contentRef.current = {content: state.content, reload: contentReload};
  useAsync(async () => {
    dispatch({type: "sync", state: "CHECKING"});
    const buildingTimeout = setTimeout(() => {
      dispatch({type: "sync", state: "BUILDING"});
    }, 1e3);
    try {
      const result = await techdocsStorageApi.syncEntityDocs({
        kind,
        namespace,
        name
      }, (log) => {
        dispatch({type: "buildLog", log});
      });
      switch (result) {
        case "updated":
          if (!contentRef.current.content) {
            contentRef.current.reload();
            dispatch({type: "sync", state: "BUILD_READY_RELOAD"});
          } else {
            dispatch({type: "sync", state: "BUILD_READY"});
          }
          break;
        case "cached":
          dispatch({type: "sync", state: "UP_TO_DATE"});
          break;
        default:
          dispatch({
            type: "sync",
            state: "ERROR",
            syncError: new Error("Unexpected return state")
          });
          break;
      }
    } catch (e) {
      dispatch({type: "sync", state: "ERROR", syncError: e});
    } finally {
      clearTimeout(buildingTimeout);
    }
  }, [kind, name, namespace, techdocsStorageApi, dispatch, contentRef]);
  const displayState = useMemo(() => calculateDisplayState({
    activeSyncState: state.activeSyncState,
    contentLoading: state.contentLoading,
    content: state.content
  }), [state.activeSyncState, state.content, state.contentLoading]);
  return {
    state: displayState,
    contentReload,
    path: state.path,
    content: state.content,
    contentErrorMessage: (_a = state.contentError) == null ? void 0 : _a.toString(),
    syncErrorMessage: (_b = state.syncError) == null ? void 0 : _b.toString(),
    buildLog: state.buildLog
  };
}

const useStyles = makeStyles(() => ({
  message: {
    wordBreak: "break-word",
    overflowWrap: "anywhere"
  }
}));
const Reader = ({entityId, onReady}) => {
  const {kind, namespace, name} = entityId;
  const theme = useTheme();
  const classes = useStyles();
  const {
    state,
    path,
    contentReload,
    content: rawPage,
    contentErrorMessage,
    syncErrorMessage,
    buildLog
  } = useReaderState(kind, namespace, name, useParams()["*"]);
  const techdocsStorageApi = useApi(techdocsStorageApiRef);
  const [sidebars, setSidebars] = useState();
  const navigate = useNavigate();
  const shadowDomRef = useRef(null);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const updateSidebarPosition = useCallback(() => {
    if (!!shadowDomRef.current && !!sidebars) {
      const shadowDiv = shadowDomRef.current;
      const shadowRoot = shadowDiv.shadowRoot || shadowDiv.attachShadow({mode: "open"});
      const mdTabs = shadowRoot.querySelector(".md-container > .md-tabs");
      sidebars.forEach((sidebar) => {
        const newTop = Math.max(shadowDomRef.current.getBoundingClientRect().top, 0);
        sidebar.style.top = mdTabs ? `${newTop + mdTabs.getBoundingClientRect().height}px` : `${newTop}px`;
      });
    }
  }, [shadowDomRef, sidebars]);
  useEffect(() => {
    updateSidebarPosition();
    window.addEventListener("scroll", updateSidebarPosition, true);
    window.addEventListener("resize", updateSidebarPosition);
    return () => {
      window.removeEventListener("scroll", updateSidebarPosition, true);
      window.removeEventListener("resize", updateSidebarPosition);
    };
  }, [updateSidebarPosition, state]);
  const preRender = useCallback((rawContent, contentPath) => transform(rawContent, [
    sanitizeDOM(),
    addBaseUrl({
      techdocsStorageApi,
      entityId: {
        kind,
        name,
        namespace
      },
      path: contentPath
    }),
    rewriteDocLinks(),
    removeMkdocsHeader(),
    simplifyMkdocsFooter(),
    addGitFeedbackLink(scmIntegrationsApi),
    injectCss({
      css: `
        body {
          font-family: ${theme.typography.fontFamily};
          --md-text-color: ${theme.palette.text.primary};
          --md-text-link-color: ${theme.palette.primary.main};

          --md-code-fg-color: ${theme.palette.text.primary};
          --md-code-bg-color: ${theme.palette.background.paper};
        }
        .md-main__inner { margin-top: 0; }
        .md-sidebar {  position: fixed; bottom: 100px; width: 20rem; }
        .md-sidebar--secondary { right: 2rem; }
        .md-content { margin-bottom: 50px }
        .md-footer { position: fixed; bottom: 0px; width: 100vw; }
        .md-footer-nav__link { width: 20rem;}
        .md-content { margin-left: 20rem; max-width: calc(100% - 20rem * 2 - 3rem); }
        .md-typeset { font-size: 1rem; }
        .md-nav { font-size: 1rem; }
        .md-grid { max-width: 90vw; margin: 0 }
        .md-typeset table:not([class]) {
          font-size: 1rem;
          border: 1px solid ${theme.palette.text.primary};
          border-bottom: none;
          border-collapse: collapse;
        }
        .md-typeset table:not([class]) td, .md-typeset table:not([class]) th {
          border-bottom: 1px solid ${theme.palette.text.primary};
        }
        .md-typeset table:not([class]) th { font-weight: bold; }
        .md-typeset .admonition, .md-typeset details {
          font-size: 1rem;
        }
        @media screen and (max-width: 76.1875em) {
          .md-nav {
            background-color: ${theme.palette.background.default};
            transition: none !important
          }
          .md-sidebar--secondary { display: none; }
          .md-sidebar--primary { left: 72px; width: 10rem }
          .md-content { margin-left: 10rem; max-width: calc(100% - 10rem); }
          .md-content__inner { font-size: 0.9rem }
          .md-footer {
            position: static;
            margin-left: 10rem;
            width: calc(100% - 10rem);
          }
          .md-nav--primary .md-nav__title {
            white-space: normal;
            height: auto;
            line-height: 1rem;
            cursor: auto;
          }
          .md-nav--primary > .md-nav__title [for="none"] {
            padding-top: 0;
          }
        }
      `
    }),
    injectCss({
      css: `
        .md-nav__link, .md-typeset a, .md-typeset a::before, .md-typeset .headerlink {
          transition: none;
        }
        `
    }),
    injectCss({
      css: `
        .md-typeset pre > code::-webkit-scrollbar-thumb {
          background-color: hsla(0, 0%, 0%, 0.32);
        }
        .md-typeset pre > code::-webkit-scrollbar-thumb:hover {
          background-color: hsla(0, 0%, 0%, 0.87);
        }
        `
    }),
    injectCss({
      css: `
        :host {
          --md-admonition-icon--note: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/></svg>');
          --md-admonition-icon--abstract: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M4 5h16v2H4V5m0 4h16v2H4V9m0 4h16v2H4v-2m0 4h10v2H4v-2z"/></svg>');
          --md-admonition-icon--info: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M13 9h-2V7h2m0 10h-2v-6h2m-1-9A10 10 0 002 12a10 10 0 0010 10 10 10 0 0010-10A10 10 0 0012 2z"/></svg>');
          --md-admonition-icon--tip: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M17.55 11.2c-.23-.3-.5-.56-.76-.82-.65-.6-1.4-1.03-2.03-1.66C13.3 7.26 13 4.85 13.91 3c-.91.23-1.75.75-2.45 1.32-2.54 2.08-3.54 5.75-2.34 8.9.04.1.08.2.08.33 0 .22-.15.42-.35.5-.22.1-.46.04-.64-.12a.83.83 0 01-.15-.17c-1.1-1.43-1.28-3.48-.53-5.12C5.89 10 5 12.3 5.14 14.47c.04.5.1 1 .27 1.5.14.6.4 1.2.72 1.73 1.04 1.73 2.87 2.97 4.84 3.22 2.1.27 4.35-.12 5.96-1.6 1.8-1.66 2.45-4.32 1.5-6.6l-.13-.26c-.2-.46-.47-.87-.8-1.25l.05-.01m-3.1 6.3c-.28.24-.73.5-1.08.6-1.1.4-2.2-.16-2.87-.82 1.19-.28 1.89-1.16 2.09-2.05.17-.8-.14-1.46-.27-2.23-.12-.74-.1-1.37.18-2.06.17.38.37.76.6 1.06.76 1 1.95 1.44 2.2 2.8.04.14.06.28.06.43.03.82-.32 1.72-.92 2.27h.01z"/></svg>');
          --md-admonition-icon--success: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>');
          --md-admonition-icon--question: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M15.07 11.25l-.9.92C13.45 12.89 13 13.5 13 15h-2v-.5c0-1.11.45-2.11 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41a2 2 0 00-2-2 2 2 0 00-2 2H8a4 4 0 014-4 4 4 0 014 4 3.2 3.2 0 01-.93 2.25M13 19h-2v-2h2M12 2A10 10 0 002 12a10 10 0 0010 10 10 10 0 0010-10c0-5.53-4.5-10-10-10z"/></svg>');
          --md-admonition-icon--warning: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M13 14h-2v-4h2m0 8h-2v-2h2M1 21h22L12 2 1 21z"/></svg>');
          --md-admonition-icon--failure: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2c5.53 0 10 4.47 10 10s-4.47 10-10 10S2 17.53 2 12 6.47 2 12 2m3.59 5L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41 15.59 7z"/></svg>');
          --md-admonition-icon--danger: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M11.5 20l4.86-9.73H13V4l-5 9.73h3.5V20M12 2c2.75 0 5.1 1 7.05 2.95C21 6.9 22 9.25 22 12s-1 5.1-2.95 7.05C17.1 21 14.75 22 12 22s-5.1-1-7.05-2.95C3 17.1 2 14.75 2 12s1-5.1 2.95-7.05C6.9 3 9.25 2 12 2z"/></svg>');
          --md-admonition-icon--bug: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M14 12h-4v-2h4m0 6h-4v-2h4m6-6h-2.81a5.985 5.985 0 00-1.82-1.96L17 4.41 15.59 3l-2.17 2.17a6.002 6.002 0 00-2.83 0L8.41 3 7 4.41l1.62 1.63C7.88 6.55 7.26 7.22 6.81 8H4v2h2.09c-.05.33-.09.66-.09 1v1H4v2h2v1c0 .34.04.67.09 1H4v2h2.81c1.04 1.79 2.97 3 5.19 3s4.15-1.21 5.19-3H20v-2h-2.09c.05-.33.09-.66.09-1v-1h2v-2h-2v-1c0-.34-.04-.67-.09-1H20V8z"/></svg>');
          --md-admonition-icon--example: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M7 13v-2h14v2H7m0 6v-2h14v2H7M7 7V5h14v2H7M3 8V5H2V4h2v4H3m-1 9v-1h3v4H2v-1h2v-.5H3v-1h1V17H2m2.25-7a.75.75 0 01.75.75c0 .2-.08.39-.21.52L3.12 13H5v1H2v-.92L4 11H2v-1h2.25z"/></svg>');
          --md-admonition-icon--quote: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M14 17h3l2-4V7h-6v6h3M6 17h3l2-4V7H5v6h3l-2 4z"/></svg>');
        }
        :host {
          --md-footnotes-icon: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.42L5.83 13H21V7h-2z"/></svg>');
        }
        :host {
          --md-details-icon: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.42z"/></svg>');
        }
        :host {
          --md-tasklist-icon: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2A10 10 0 002 12a10 10 0 0010 10 10 10 0 0010-10A10 10 0 0012 2z"/></svg>');
          --md-tasklist-icon--checked: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2m-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>');
        }
        `
    })
  ]), [
    kind,
    name,
    namespace,
    scmIntegrationsApi,
    techdocsStorageApi,
    theme.palette.background.default,
    theme.palette.background.paper,
    theme.palette.primary.main,
    theme.palette.text.primary,
    theme.typography.fontFamily
  ]);
  const postRender = useCallback(async (shadowRoot) => transform(shadowRoot.children[0], [
    (dom) => {
      setTimeout(() => {
        var _a;
        if (window.location.hash) {
          const hash = window.location.hash.slice(1);
          (_a = shadowRoot == null ? void 0 : shadowRoot.getElementById(hash)) == null ? void 0 : _a.scrollIntoView();
        }
      }, 200);
      return dom;
    },
    addLinkClickListener({
      baseUrl: window.location.origin,
      onClick: (_, url) => {
        var _a;
        const parsedUrl = new URL(url);
        if (parsedUrl.hash) {
          navigate(`${parsedUrl.pathname}${parsedUrl.hash}`);
          (_a = shadowRoot == null ? void 0 : shadowRoot.getElementById(parsedUrl.hash.slice(1))) == null ? void 0 : _a.scrollIntoView();
        } else {
          navigate(parsedUrl.pathname);
        }
      }
    }),
    onCssReady({
      docStorageUrl: await techdocsStorageApi.getApiOrigin(),
      onLoading: (dom) => {
        dom.style.setProperty("opacity", "0");
      },
      onLoaded: (dom) => {
        var _a;
        dom.style.removeProperty("opacity");
        (_a = dom.querySelector(".md-nav__title")) == null ? void 0 : _a.removeAttribute("for");
        const sideDivs = Array.from(shadowRoot.querySelectorAll(".md-sidebar"));
        setSidebars(sideDivs);
        const docTopPosition = dom.getBoundingClientRect().top;
        const mdTabs = dom.querySelector(".md-container > .md-tabs");
        sideDivs.forEach((sidebar) => {
          sidebar.style.top = mdTabs ? `${docTopPosition + mdTabs.getBoundingClientRect().height}px` : `${docTopPosition}px`;
        });
      }
    })
  ]), [navigate, techdocsStorageApi]);
  useEffect(() => {
    var _a;
    if (!rawPage || !shadowDomRef.current) {
      if ((_a = shadowDomRef.current) == null ? void 0 : _a.shadowRoot) {
        shadowDomRef.current.shadowRoot.innerHTML = "";
      }
      return () => {
      };
    }
    if (onReady) {
      onReady();
    }
    let shouldReplaceContent = true;
    preRender(rawPage, path).then(async (transformedElement) => {
      if (!(transformedElement == null ? void 0 : transformedElement.innerHTML)) {
        return;
      }
      if (!shouldReplaceContent) {
        return;
      }
      const shadowDiv = shadowDomRef.current;
      const shadowRoot = shadowDiv.shadowRoot || shadowDiv.attachShadow({mode: "open"});
      Array.from(shadowRoot.children).forEach((child) => shadowRoot.removeChild(child));
      shadowRoot.appendChild(transformedElement);
      window.scroll({top: 0});
      await postRender(shadowRoot);
    });
    return () => {
      shouldReplaceContent = false;
    };
  }, [onReady, path, postRender, preRender, rawPage]);
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, state === "CHECKING" && /* @__PURE__ */ React__default.createElement(Progress, null), state === "INITIAL_BUILD" && /* @__PURE__ */ React__default.createElement(Alert, {
    variant: "outlined",
    severity: "info",
    icon: /* @__PURE__ */ React__default.createElement(CircularProgress, {
      size: "24px"
    }),
    action: /* @__PURE__ */ React__default.createElement(TechDocsBuildLogs, {
      buildLog
    })
  }, "Documentation is accessed for the first time and is being prepared. The subsequent loads are much faster."), state === "CONTENT_STALE_REFRESHING" && /* @__PURE__ */ React__default.createElement(Alert, {
    variant: "outlined",
    severity: "info",
    icon: /* @__PURE__ */ React__default.createElement(CircularProgress, {
      size: "24px"
    }),
    action: /* @__PURE__ */ React__default.createElement(TechDocsBuildLogs, {
      buildLog
    })
  }, "A newer version of this documentation is being prepared and will be available shortly."), state === "CONTENT_STALE_READY" && /* @__PURE__ */ React__default.createElement(Alert, {
    variant: "outlined",
    severity: "success",
    action: /* @__PURE__ */ React__default.createElement(Button$1, {
      color: "inherit",
      onClick: () => contentReload()
    }, "Refresh")
  }, "A newer version of this documentation is now available, please refresh to view."), state === "CONTENT_STALE_ERROR" && /* @__PURE__ */ React__default.createElement(Alert, {
    variant: "outlined",
    severity: "error",
    action: /* @__PURE__ */ React__default.createElement(TechDocsBuildLogs, {
      buildLog
    }),
    classes: {message: classes.message}
  }, "Building a newer version of this documentation failed.", " ", syncErrorMessage), state === "CONTENT_NOT_FOUND" && /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, syncErrorMessage && /* @__PURE__ */ React__default.createElement(Alert, {
    variant: "outlined",
    severity: "error",
    action: /* @__PURE__ */ React__default.createElement(TechDocsBuildLogs, {
      buildLog
    }),
    classes: {message: classes.message}
  }, "Building a newer version of this documentation failed.", " ", syncErrorMessage), /* @__PURE__ */ React__default.createElement(TechDocsNotFound, {
    errorMessage: contentErrorMessage
  })), /* @__PURE__ */ React__default.createElement("div", {
    "data-testid": "techdocs-content-shadowroot",
    ref: shadowDomRef
  }));
};

const DocsCardGrid = ({
  entities
}) => {
  if (!entities)
    return null;
  return /* @__PURE__ */ React__default.createElement(ItemCardGrid, {
    "data-testid": "docs-explore"
  }, !(entities == null ? void 0 : entities.length) ? null : entities.map((entity, index) => {
    var _a;
    return /* @__PURE__ */ React__default.createElement(Card, {
      key: index
    }, /* @__PURE__ */ React__default.createElement(CardMedia, null, /* @__PURE__ */ React__default.createElement(ItemCardHeader, {
      title: entity.metadata.name
    })), /* @__PURE__ */ React__default.createElement(CardContent, null, entity.metadata.description), /* @__PURE__ */ React__default.createElement(CardActions, null, /* @__PURE__ */ React__default.createElement(Button, {
      to: generatePath(rootDocsRouteRef.path, {
        namespace: (_a = entity.metadata.namespace) != null ? _a : "default",
        kind: entity.kind,
        name: entity.metadata.name
      }),
      color: "primary"
    }, "Read Docs")));
  }));
};

var DocsCardGrid$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DocsCardGrid: DocsCardGrid
});

const panels = {
  DocsTable: DocsTable$1,
  DocsCardGrid
};
const CustomPanel = ({
  config,
  entities,
  index
}) => {
  const useStyles = makeStyles({
    panelContainer: {
      marginBottom: "2rem",
      ...config.panelCSS ? config.panelCSS : {}
    }
  });
  const classes = useStyles();
  const {value: user} = useOwnUser();
  const Panel = panels[config.panelType];
  const shownEntities = entities.filter((entity) => {
    if (config.filterPredicate === "ownedByUser") {
      if (!user) {
        return false;
      }
      return isOwnerOf(user, entity);
    }
    return typeof config.filterPredicate === "function" && config.filterPredicate(entity);
  });
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement(ContentHeader, {
    title: config.title,
    description: config.description
  }, index === 0 ? /* @__PURE__ */ React__default.createElement(SupportButton, null, "Discover documentation in your ecosystem.") : null), /* @__PURE__ */ React__default.createElement("div", {
    className: classes.panelContainer
  }, /* @__PURE__ */ React__default.createElement(Panel, {
    entities: shownEntities
  })));
};
const TechDocsCustomHome = ({
  tabsConfig
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const catalogApi = useApi(catalogApiRef);
  const {
    value: entities,
    loading,
    error
  } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        "metadata.annotations.backstage.io/techdocs-ref": CATALOG_FILTER_EXISTS
      },
      fields: [
        "apiVersion",
        "kind",
        "metadata",
        "relations",
        "spec.owner",
        "spec.type"
      ]
    });
    return response.items.filter((entity) => {
      var _a;
      return !!((_a = entity.metadata.annotations) == null ? void 0 : _a["backstage.io/techdocs-ref"]);
    });
  });
  const currentTabConfig = tabsConfig[selectedTab];
  if (loading) {
    return /* @__PURE__ */ React__default.createElement(TechDocsPageWrapper, null, /* @__PURE__ */ React__default.createElement(Content, null, /* @__PURE__ */ React__default.createElement(Progress, null)));
  }
  if (error) {
    return /* @__PURE__ */ React__default.createElement(TechDocsPageWrapper, null, /* @__PURE__ */ React__default.createElement(Content, null, /* @__PURE__ */ React__default.createElement(WarningPanel, {
      severity: "error",
      title: "Could not load available documentation."
    }, /* @__PURE__ */ React__default.createElement(CodeSnippet, {
      language: "text",
      text: error.toString()
    }))));
  }
  return /* @__PURE__ */ React__default.createElement(TechDocsPageWrapper, null, /* @__PURE__ */ React__default.createElement(HeaderTabs, {
    selectedIndex: selectedTab,
    onChange: (index) => setSelectedTab(index),
    tabs: tabsConfig.map(({label}, index) => ({
      id: index.toString(),
      label
    }))
  }), /* @__PURE__ */ React__default.createElement(Content, null, currentTabConfig.panels.map((config, index) => /* @__PURE__ */ React__default.createElement(CustomPanel, {
    key: index,
    config,
    entities: !!entities ? entities : [],
    index
  }))));
};

var TechDocsCustomHome$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  TechDocsCustomHome: TechDocsCustomHome
});

const LegacyTechDocsHome = () => {
  const tabsConfig = [
    {
      label: "Overview",
      panels: [
        {
          title: "Overview",
          description: "Explore your internal technical ecosystem through documentation.",
          panelType: "DocsCardGrid",
          filterPredicate: () => true
        }
      ]
    },
    {
      label: "Owned Documents",
      panels: [
        {
          title: "Owned documents",
          description: "Access your documentation.",
          panelType: "DocsTable",
          filterPredicate: "ownedByUser"
        }
      ]
    }
  ];
  return /* @__PURE__ */ React__default.createElement(TechDocsCustomHome, {
    tabsConfig
  });
};

const TechDocsIndexPage = () => {
  const outlet = useOutlet();
  return outlet || /* @__PURE__ */ React__default.createElement(LegacyTechDocsHome, null);
};

var TechDocsIndexPage$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  TechDocsIndexPage: TechDocsIndexPage
});

const TechDocsPageHeader = ({
  entityId,
  entityMetadata,
  techDocsMetadata
}) => {
  const {name} = entityId;
  const {site_name: siteName, site_description: siteDescription} = techDocsMetadata || {};
  const {locationMetadata, spec} = entityMetadata || {};
  const lifecycle = spec == null ? void 0 : spec.lifecycle;
  const ownedByRelations = entityMetadata ? getEntityRelations(entityMetadata, RELATION_OWNED_BY) : [];
  const docsRootLink = useRouteRef(rootRouteRef)();
  const labels = /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement(HeaderLabel, {
    label: "Component",
    value: /* @__PURE__ */ React__default.createElement(EntityRefLink, {
      color: "inherit",
      entityRef: entityId,
      defaultKind: "Component"
    })
  }), ownedByRelations.length > 0 && /* @__PURE__ */ React__default.createElement(HeaderLabel, {
    label: "Owner",
    value: /* @__PURE__ */ React__default.createElement(EntityRefLinks, {
      color: "inherit",
      entityRefs: ownedByRelations,
      defaultKind: "group"
    })
  }), lifecycle ? /* @__PURE__ */ React__default.createElement(HeaderLabel, {
    label: "Lifecycle",
    value: lifecycle
  }) : null, locationMetadata && locationMetadata.type !== "dir" && locationMetadata.type !== "file" ? /* @__PURE__ */ React__default.createElement(HeaderLabel, {
    label: "",
    value: /* @__PURE__ */ React__default.createElement("a", {
      href: locationMetadata.target,
      target: "_blank",
      rel: "noopener noreferrer"
    }, /* @__PURE__ */ React__default.createElement(CodeIcon, {
      style: {marginTop: "-25px", fill: "#fff"}
    }))
  }) : null);
  return /* @__PURE__ */ React__default.createElement(Header, {
    title: siteName ? siteName : ".",
    pageTitleOverride: siteName || name,
    subtitle: siteDescription && siteDescription !== "None" ? siteDescription : "",
    type: "Docs",
    typeLink: docsRootLink
  }, labels);
};

const TechDocsPage = () => {
  const [documentReady, setDocumentReady] = useState(false);
  const {namespace, kind, name} = useParams();
  const techdocsApi = useApi(techdocsApiRef);
  const {value: techdocsMetadataValue} = useAsync(() => {
    if (documentReady) {
      return techdocsApi.getTechDocsMetadata({kind, namespace, name});
    }
    return Promise.resolve(void 0);
  }, [kind, namespace, name, techdocsApi, documentReady]);
  const {value: entityMetadataValue} = useAsync(() => {
    return techdocsApi.getEntityMetadata({kind, namespace, name});
  }, [kind, namespace, name, techdocsApi]);
  const onReady = useCallback(() => {
    setDocumentReady(true);
  }, [setDocumentReady]);
  return /* @__PURE__ */ React__default.createElement(Page, {
    themeId: "documentation"
  }, /* @__PURE__ */ React__default.createElement(TechDocsPageHeader, {
    techDocsMetadata: techdocsMetadataValue,
    entityMetadata: entityMetadataValue,
    entityId: {
      kind,
      namespace,
      name
    }
  }), /* @__PURE__ */ React__default.createElement(Content, {
    "data-testid": "techdocs-content"
  }, /* @__PURE__ */ React__default.createElement(Reader, {
    onReady,
    entityId: {
      kind,
      namespace,
      name
    }
  })));
};

var TechDocsPage$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  TechDocsPage: TechDocsPage
});

const EntityPageDocs = ({entity}) => {
  var _a;
  return /* @__PURE__ */ React__default.createElement(Reader, {
    entityId: {
      kind: entity.kind,
      namespace: (_a = entity.metadata.namespace) != null ? _a : "default",
      name: entity.metadata.name
    }
  });
};

const TECHDOCS_ANNOTATION = "backstage.io/techdocs-ref";
const Router = () => {
  return /* @__PURE__ */ React__default.createElement(Routes, null, /* @__PURE__ */ React__default.createElement(Route, {
    path: `/${rootRouteRef.path}`,
    element: /* @__PURE__ */ React__default.createElement(TechDocsIndexPage, null)
  }), /* @__PURE__ */ React__default.createElement(Route, {
    path: `/${rootDocsRouteRef.path}`,
    element: /* @__PURE__ */ React__default.createElement(TechDocsPage, null)
  }));
};
const EmbeddedDocsRouter = (_props) => {
  var _a;
  const {entity} = useEntity();
  const projectId = (_a = entity.metadata.annotations) == null ? void 0 : _a[TECHDOCS_ANNOTATION];
  if (!projectId) {
    return /* @__PURE__ */ React__default.createElement(MissingAnnotationEmptyState, {
      annotation: TECHDOCS_ANNOTATION
    });
  }
  return /* @__PURE__ */ React__default.createElement(Routes, null, /* @__PURE__ */ React__default.createElement(Route, {
    path: `/${rootCatalogDocsRouteRef.path}`,
    element: /* @__PURE__ */ React__default.createElement(EntityPageDocs, {
      entity
    })
  }));
};

var Router$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Router: Router,
  EmbeddedDocsRouter: EmbeddedDocsRouter
});

export { DefaultTechDocsHome, DocsCardGrid$2 as DocsCardGrid, DocsResultListItem, DocsTable, EmbeddedDocsRouter, EntityListDocsTable, EntityTechdocsContent, Reader, Router, TechDocsClient, TechDocsCustomHome$2 as TechDocsCustomHome, TechDocsIndexPage$2 as TechDocsIndexPage, TechDocsPageWrapper, TechDocsPicker, TechDocsReaderPage, TechDocsStorageClient, TechdocsPage, techdocsPlugin as plugin, techdocsApiRef, techdocsPlugin, techdocsStorageApiRef };
//# sourceMappingURL=index.esm.js.map
